import React from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

import NavBar from '@/components/layout/NavBar/NavBar';
import Footer from '@/components/layout/Footer/Footer';
import ProtectedRoutes from '@/components/common/ProtectedRoutes';
import PublicRoutes from '@/components/common/PublicRoutes';
import AuthRestrictedRoutes from '@/components/common/AuthRestrictedRoutes';
import ChatWidget from '@/components/common/ChatWidget';
import TourResumeBanner from '@/components/common/TourResumeBanner';
import PreviewBanner from '@/components/common/PreviewBanner';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import { POSITIONS_ROLES } from '@/shared/utils/positionsAccess';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';

// Public pages
import HomePage from '@/features/home/HomePage';
import AboutPage from '@/features/about/AboutPage';

// Auth pages
import LogIn from '@/features/auth/components/LogIn';
import SignUp from '@/features/auth/components/SignUp';
import ForgotPassword from '@/features/auth/components/ForgotPassword';

// Protected pages
import Dashboard from '@/features/dashboard/Dashboard';
import CreateProfile from '@/features/profile/components/CreateProfile';
import ProfilePage from '@/features/profile/components/ProfilePage';
import EditProfile from '@/features/profile/components/EditProfile';
import ApplicationsPage from '@/features/applications/ApplicationsPage';
import CreateApplicationPage from '@/features/applications/faculty/CreateApplicationPage';
import StartApplicationPage from '@/features/applications/faculty/StartApplicationPage';
import StudentApplicationPage from '@/features/applications/student/StudentApplicationPage';
import CoursesPage from '@/features/courses/CoursesPage';
import AssignmentsPage from '@/features/assignments/AssignmentsPage';
import TrainingPage from '@/features/training/TrainingPage';
import SurveyListPage from '@/features/surveys/SurveyListPage';
import SurveyFormPage from '@/features/surveys/SurveyFormPage';
import SurveyResultsPage from '@/features/surveys/SurveyResultsPage';
import SurveyBuilderPage from '@/features/surveys/SurveyBuilderPage';
import ApplicationDetailPage from '@/features/applications/faculty/ApplicationDetailPage';
import StudentApplicationDetailPage from '@/features/applications/student/StudentApplicationDetailPage';
import MyStudentApplicationPage from '@/features/applications/student/MyStudentApplicationPage';
import OpenPositionsPage from '@/features/positions/OpenPositionsPage';
import MyPositionsPage from '@/features/positions/MyPositionsPage';

// Program-specific pages
import SICoordinatorDashboard from '@/features/programs/si/SICoordinatorDashboard';
import TACoordinatorDashboard from '@/features/programs/ta/TACoordinatorDashboard';
import LACoordinatorDashboard from '@/features/programs/la/LACoordinatorDashboard';

// Analytics
import AnalyticsPage from '@/features/analytics/AnalyticsPage';

// Admin pages
import AdminPage from '@/features/admin/AdminPage';
import FeedbackPage from '@/features/feedback/FeedbackPage';
import FeedbackScreenshotPage from '@/features/feedback/FeedbackScreenshotPage';
import NotificationsPage from '@/features/notifications/NotificationsPage';

/**
 * Faculty, coordinators and admins get Piper on every page; everyone else gets
 * Rowan. Roles come from the effective roles, so previewing as a student shows
 * Rowan.
 */
function useChatPersona() {
    const { userProfile, effectiveRoles } = useAuth();
    const roles = resolveRoles({ effectiveRoles, userProfile });

    const isStaff = roles.some((role) =>
        [
            'faculty',
            'si_coordinator',
            'ta_coordinator',
            'la_coordinator',
            'admin',
        ].includes(role)
    );

    return isStaff ? 'piper' : 'rowan';
}

function App() {
    const location = useLocation();
    const chatPersona = useChatPersona();

    // The student application workflow is unfinished, so its routes are only
    // registered when the flag is on. Off means the paths do not exist here at
    // all, matching the server, which refuses the same endpoints.
    const studentApplicationEnabled = useFeature('studentApplication');

    // The student hiring pages need both flags: the profile routes they read
    // sit behind studentApplication on the server, and the position and
    // candidacy routes behind hiring.
    const hiringEnabled = useFeature('hiring');

    // The three program dashboards are the coordinatorReview feature. Same
    // rule: off means the routes are not registered, for every role including
    // admin, so a direct URL falls through to no match. On changes nothing
    // about who reaches them; each keeps its own requiredRole.
    const coordinatorReviewEnabled = useFeature('coordinatorReview');

    // Surveys and training are not in the launch (#391). Each has its own
    // flag so one can come back without the other, and off means its routes
    // are not registered, like the two above.
    const surveysEnabled = useFeature('surveys');
    const trainingEnabled = useFeature('training');

    // The Piper and Rowan assistants have no AI behind them yet (#391). With
    // the flag off the widget is not rendered at all, on any page.
    const assistantsEnabled = useFeature('assistants');

    // Pages where we don't show the chat widget
    const hideChatRoutes = [
        '/login',
        '/signup',
        '/forgot-password',
        '/create-profile',
    ];
    const showChat =
        assistantsEnabled && !hideChatRoutes.includes(location.pathname);

    return (
        <div className="App">
            {/*
              WCAG 2.4.1. The navigation is the first thing in the tab order on
              every route, so without this a keyboard or screen-reader user tabs
              through the whole menu again on each page before reaching the
              content they came for. It stays hidden until focused, so it costs
              a sighted mouse user nothing.
            */}
            <a href="#App-main" className="psh-skip-link">
                Skip to main content
            </a>
            <PreviewBanner />
            <header id="App-header">
                <NavBar />
            </header>
            <TourResumeBanner />
            <main id="App-main" tabIndex={-1}>
                <Routes>
                    {/* Public routes */}
                    <Route
                        path="/"
                        element={
                            <PublicRoutes>
                                <HomePage />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/about"
                        element={
                            <PublicRoutes>
                                <AboutPage />
                            </PublicRoutes>
                        }
                    />

                    {/* Auth-Restricted Routes (unauthenticated users only) */}
                    <Route
                        path="/login"
                        element={
                            <AuthRestrictedRoutes>
                                <LogIn />
                            </AuthRestrictedRoutes>
                        }
                    />
                    <Route
                        path="/signup"
                        element={
                            <AuthRestrictedRoutes>
                                <SignUp />
                            </AuthRestrictedRoutes>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <AuthRestrictedRoutes>
                                <ForgotPassword />
                            </AuthRestrictedRoutes>
                        }
                    />

                    {/* Protected Routes (authenticated users only) */}
                    <Route
                        path="/create-profile"
                        element={
                            <ProtectedRoutes>
                                <CreateProfile />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoutes>
                                <Dashboard />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoutes>
                                <ProfilePage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/profile/edit"
                        element={
                            <ProtectedRoutes>
                                <EditProfile />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/applications/start"
                        element={
                            <ProtectedRoutes
                                requiredRole={['faculty', 'admin']}
                            >
                                <StartApplicationPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/applications/new"
                        element={
                            <ProtectedRoutes
                                requiredRole={['faculty', 'admin']}
                            >
                                <CreateApplicationPage />
                            </ProtectedRoutes>
                        }
                    />
                    {studentApplicationEnabled && (
                        <Route
                            path="/apply"
                            element={
                                <ProtectedRoutes
                                    requiredRole={['student', 'admin']}
                                >
                                    <StudentApplicationPage />
                                </ProtectedRoutes>
                            }
                        />
                    )}
                    {studentApplicationEnabled && (
                        <Route
                            path="/apply/:id/edit"
                            element={
                                <ProtectedRoutes
                                    requiredRole={['student', 'admin']}
                                >
                                    <StudentApplicationPage />
                                </ProtectedRoutes>
                            }
                        />
                    )}
                    {studentApplicationEnabled && hiringEnabled && (
                        <Route
                            path="/positions"
                            element={
                                <ProtectedRoutes requiredRole={POSITIONS_ROLES}>
                                    <OpenPositionsPage />
                                </ProtectedRoutes>
                            }
                        />
                    )}
                    {studentApplicationEnabled && hiringEnabled && (
                        <Route
                            path="/my-applications/:id"
                            element={
                                <ProtectedRoutes
                                    requiredRole={['student', 'admin']}
                                >
                                    <MyStudentApplicationPage />
                                </ProtectedRoutes>
                            }
                        />
                    )}
                    {hiringEnabled && (
                        <Route
                            path="/my-positions/:applicationId?/:candidacyId?"
                            element={
                                <ProtectedRoutes
                                    requiredRole={['faculty', 'admin']}
                                >
                                    <MyPositionsPage />
                                </ProtectedRoutes>
                            }
                        />
                    )}

                    <Route
                        path="/applications/:id"
                        element={
                            <ProtectedRoutes
                                requiredRole={[
                                    'faculty',
                                    'admin',
                                    'la_coordinator',
                                    'si_coordinator',
                                    'ta_coordinator',
                                ]}
                            >
                                <ApplicationDetailPage />
                            </ProtectedRoutes>
                        }
                    />

                    {studentApplicationEnabled && (
                        <Route
                            path="/student-applications/:id"
                            element={
                                <ProtectedRoutes
                                    requiredRole={[
                                        'si_coordinator',
                                        'la_coordinator',
                                        'admin',
                                    ]}
                                >
                                    <StudentApplicationDetailPage />
                                </ProtectedRoutes>
                            }
                        />
                    )}

                    <Route
                        path="/applications/*"
                        element={
                            <ProtectedRoutes>
                                <ApplicationsPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/courses/*"
                        element={
                            <ProtectedRoutes>
                                <CoursesPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/feedback"
                        element={
                            <ProtectedRoutes>
                                <FeedbackPage />
                            </ProtectedRoutes>
                        }
                    />
                    {/* Every role: students get offer notices too */}
                    <Route
                        path="/notifications"
                        element={
                            <ProtectedRoutes>
                                <NotificationsPage />
                            </ProtectedRoutes>
                        }
                    />
                    {/* Filed feedback links here for its screenshot */}
                    <Route
                        path="/feedback/screenshots/:id"
                        element={
                            <ProtectedRoutes requiredRole="admin">
                                <FeedbackScreenshotPage />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/assignments/*"
                        element={
                            <ProtectedRoutes>
                                <AssignmentsPage />
                            </ProtectedRoutes>
                        }
                    />
                    {trainingEnabled && (
                        <Route
                            path="/training/*"
                            element={
                                <ProtectedRoutes>
                                    <TrainingPage />
                                </ProtectedRoutes>
                            }
                        />
                    )}

                    {/* Survey Routes */}
                    {surveysEnabled && (
                        <>
                            <Route
                                path="/surveys/new"
                                element={
                                    <ProtectedRoutes
                                        requiredRole={[
                                            'si_coordinator',
                                            'ta_coordinator',
                                            'la_coordinator',
                                            'admin',
                                        ]}
                                    >
                                        <SurveyBuilderPage />
                                    </ProtectedRoutes>
                                }
                            />
                            <Route
                                path="/surveys/:id/results"
                                element={
                                    <ProtectedRoutes
                                        requiredRole={[
                                            'si_coordinator',
                                            'ta_coordinator',
                                            'la_coordinator',
                                            'admin',
                                        ]}
                                    >
                                        <SurveyResultsPage />
                                    </ProtectedRoutes>
                                }
                            />
                            <Route
                                path="/surveys/:id"
                                element={
                                    <ProtectedRoutes>
                                        <SurveyFormPage />
                                    </ProtectedRoutes>
                                }
                            />
                            <Route
                                path="/surveys"
                                element={
                                    <ProtectedRoutes>
                                        <SurveyListPage />
                                    </ProtectedRoutes>
                                }
                            />
                        </>
                    )}

                    {/* Program Coordinator Routes */}
                    {coordinatorReviewEnabled && (
                        <Route
                            path="/programs/si/*"
                            element={
                                <ProtectedRoutes requiredRole="si_coordinator">
                                    <SICoordinatorDashboard />
                                </ProtectedRoutes>
                            }
                        />
                    )}
                    {coordinatorReviewEnabled && (
                        <Route
                            path="/programs/ta/*"
                            element={
                                <ProtectedRoutes requiredRole="ta_coordinator">
                                    <TACoordinatorDashboard />
                                </ProtectedRoutes>
                            }
                        />
                    )}
                    {coordinatorReviewEnabled && (
                        <Route
                            path="/programs/la/*"
                            element={
                                <ProtectedRoutes requiredRole="la_coordinator">
                                    <LACoordinatorDashboard />
                                </ProtectedRoutes>
                            }
                        />
                    )}

                    {/* Analytics Routes */}
                    <Route
                        path="/analytics/*"
                        element={
                            <ProtectedRoutes
                                requiredRole={[
                                    'si_coordinator',
                                    'ta_coordinator',
                                    'la_coordinator',
                                    'admin',
                                ]}
                            >
                                <AnalyticsPage />
                            </ProtectedRoutes>
                        }
                    />

                    {/* Admin Routes */}
                    <Route
                        path="/admin/*"
                        element={
                            <ProtectedRoutes requiredRole="admin">
                                <AdminPage />
                            </ProtectedRoutes>
                        }
                    />
                </Routes>
            </main>
            <footer id="App-footer">
                <Footer />
            </footer>

            {/* AI Chat Widget - shows Piper for faculty, Rowan for students */}
            {showChat && <ChatWidget persona={chatPersona} comingSoon={true} />}
        </div>
    );
}

export default App;
